import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { LocationProvider } from "./context/LocationContext";
import { AIProcessingProvider } from "./context/AIProcessingContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminProtectedRoute } from "./components/AdminProtectedRoute";
import { AIProgressBar } from "./components/AIProgressBar";
import { CursorToastProvider, CursorToastBridge } from "./components/CursorToast";

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import WelcomePage from "./pages/WelcomePage";
import DashboardPage from "./pages/DashboardPage";
import WanterDashboard from "./pages/WanterDashboard";
import OwnerDashboard from "./pages/OwnerDashboard";
import CreateWTBPage from "./pages/CreateWTBPage";
import AddInventoryPage from "./pages/AddInventoryPage";
import ListLandingPage from "./pages/ListLandingPage";
import InventoryDetailPage from "./pages/InventoryDetailPage";
import CatalogItemDetailPage from "./pages/CatalogItemDetailPage";
import PurchaseHistoryPage from "./pages/PurchaseHistoryPage";
import BillingPage from "./pages/BillingPage";
import NotificationsPage from "./pages/NotificationsPage";
import WTBDetailPage from "./pages/WTBDetailPage";
import WantMatchesPage from "./pages/WantMatchesPage";
import ItemMatchesPage from "./pages/ItemMatchesPage";
import MatchDetailPage from "./pages/MatchDetailPage";
import MessagesPage from "./pages/MessagesPage";
import ProfilePage from "./pages/ProfilePage";
import HowItWorksPage from "./pages/HowItWorksPage";
import AboutUsPage from "./pages/AboutUsPage";
import HelpPage from "./pages/HelpPage";
import TermsPage from "./pages/TermsPage";
import CommunityGuidelinesPage from "./pages/CommunityGuidelinesPage";
import PrivacyPage from "./pages/PrivacyPage";
import PremiumPage from "./pages/PremiumPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AdminPage from "./pages/AdminPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import AdminReportsPage from "./pages/AdminReportsPage";
import AdminCommunityReportsPage from "./pages/AdminCommunityReportsPage";
import AdminDetectionsPage from "./pages/AdminDetectionsPage";
import AdminDisciplinePage from "./pages/AdminDisciplinePage";
import AdminAuditLogsPage from "./pages/AdminAuditLogsPage";
import AdminAnalyticsPage from "./pages/AdminAnalyticsPage";
import AdminRevenuePage from "./pages/AdminRevenuePage";
import AdminSubscriptionsPage from "./pages/AdminSubscriptionsPage";
import AdminBillingPage from "./pages/AdminBillingPage";
import NotFoundPage from "./pages/NotFoundPage";
import FacebookCallbackPage from "./pages/FacebookCallbackPage";
import PublicProfilePage from "./pages/PublicProfilePage";
import SavedItemsPage from "./pages/SavedItemsPage";
import PointsPage from "./pages/PointsPage";
import WalletPage from "./pages/WalletPage";
import PreAuthConfirmPage from "./pages/PreAuthConfirmPage";
import PreAuthRemainderConfirmPage from "./pages/PreAuthRemainderConfirmPage";
import PetsLandingPage from "./pages/PetsLandingPage";
import AddPetListingPage from "./pages/AddPetListingPage";
import PetsTermsPage from "./pages/PetsTermsPage";
import AutoHubPage from "./pages/AutoHubPage";
import AutoLandingPage from "./pages/AutoLandingPage";
import AutoListLandingPage from "./pages/AutoListLandingPage";
import AddVehicleListingPage from "./pages/AddVehicleListingPage";
import AddFreeVehicleListingPage from "./pages/AddFreeVehicleListingPage";
import AutoTermsPage from "./pages/AutoTermsPage";

import "./App.css";

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <LocationProvider>
            <AIProcessingProvider>
              <CursorToastProvider>
                <CursorToastBridge />
            <AIProgressBar />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/facebook/callback" element={<FacebookCallbackPage />} />
              <Route path="/wtb/:id" element={<WTBDetailPage />} />
              <Route path="/catalog/:id" element={<CatalogItemDetailPage />} />
              <Route path="/user/:userId" element={<PublicProfilePage />} />
              <Route path="/saved" element={<SavedItemsPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              {/* WANTD Pets — public routes */}
              <Route path="/pets" element={<PetsLandingPage />} />
              <Route path="/pets/terms" element={<PetsTermsPage />} />
              <Route path="/pets/:id" element={<CatalogItemDetailPage />} />
              {/* WANTD Auto — public routes */}
              <Route path="/auto" element={<AutoHubPage />} />
              <Route path="/auto/cars" element={<AutoLandingPage slug="cars" />} />
              <Route path="/auto/cars/:id" element={<CatalogItemDetailPage />} />
              {/* Non-cars subcategories — coming soon, 404 until launch */}
              <Route path="/auto/motorbikes" element={<NotFoundPage />} />
              <Route path="/auto/motorbikes/:id" element={<NotFoundPage />} />
              <Route path="/auto/motorbikes/list" element={<NotFoundPage />} />
              <Route path="/auto/motorbikes/list/free" element={<NotFoundPage />} />
              <Route path="/auto/motorbikes/list/plus" element={<NotFoundPage />} />
              <Route path="/auto/caravans" element={<NotFoundPage />} />
              <Route path="/auto/caravans/:id" element={<NotFoundPage />} />
              <Route path="/auto/caravans/list" element={<NotFoundPage />} />
              <Route path="/auto/caravans/list/free" element={<NotFoundPage />} />
              <Route path="/auto/caravans/list/plus" element={<NotFoundPage />} />
              <Route path="/auto/boats" element={<NotFoundPage />} />
              <Route path="/auto/boats/:id" element={<NotFoundPage />} />
              <Route path="/auto/boats/list" element={<NotFoundPage />} />
              <Route path="/auto/boats/list/free" element={<NotFoundPage />} />
              <Route path="/auto/boats/list/plus" element={<NotFoundPage />} />
              <Route path="/auto/trucks" element={<NotFoundPage />} />
              <Route path="/auto/trucks/:id" element={<NotFoundPage />} />
              <Route path="/auto/trucks/list" element={<NotFoundPage />} />
              <Route path="/auto/trucks/list/free" element={<NotFoundPage />} />
              <Route path="/auto/trucks/list/plus" element={<NotFoundPage />} />
              <Route path="/auto/commercial" element={<NotFoundPage />} />
              <Route path="/auto/commercial/:id" element={<NotFoundPage />} />
              <Route path="/auto/commercial/list" element={<NotFoundPage />} />
              <Route path="/auto/commercial/list/free" element={<NotFoundPage />} />
              <Route path="/auto/commercial/list/plus" element={<NotFoundPage />} />
              <Route path="/auto/motorsport" element={<NotFoundPage />} />
              <Route path="/auto/motorsport/list" element={<NotFoundPage />} />
              <Route path="/auto/motorsport/list/free" element={<NotFoundPage />} />
              <Route path="/auto/motorsport/list/plus" element={<NotFoundPage />} />
              <Route path="/auto/parts" element={<NotFoundPage />} />
              <Route path="/auto/parts/list" element={<NotFoundPage />} />
              <Route path="/auto/parts/list/free" element={<NotFoundPage />} />
              <Route path="/auto/parts/list/plus" element={<NotFoundPage />} />
              <Route path="/auto/terms" element={<AutoTermsPage />} />
              {/* Backwards-compat: old /{slug} browse routes redirect to /auto/{slug} */}
              <Route path="/cars" element={<Navigate to="/auto/cars" replace />} />
              <Route path="/motorbikes" element={<Navigate to="/auto/motorbikes" replace />} />
              <Route path="/boats" element={<Navigate to="/auto/boats" replace />} />
              <Route path="/caravans" element={<Navigate to="/auto/caravans" replace />} />
              <Route path="/trucks" element={<Navigate to="/auto/trucks" replace />} />
              <Route path="/commercial" element={<Navigate to="/auto/commercial" replace />} />
              {/* Old /{slug}/:id detail links still work — same component */}
              <Route path="/cars/:id" element={<CatalogItemDetailPage />} />
              <Route path="/motorbikes/:id" element={<CatalogItemDetailPage />} />
              <Route path="/boats/:id" element={<CatalogItemDetailPage />} />
              <Route path="/caravans/:id" element={<CatalogItemDetailPage />} />
              <Route path="/trucks/:id" element={<CatalogItemDetailPage />} />
              <Route path="/commercial/:id" element={<CatalogItemDetailPage />} />
              <Route path="/welcome" element={
                <ProtectedRoute>
                  <WelcomePage />
                </ProtectedRoute>
              } />
              
              {/* WANTD Pets — protected routes */}
              <Route path="/pets/list" element={
                <ProtectedRoute>
                  <AddPetListingPage />
                </ProtectedRoute>
              } />

              {/* WANTD Auto — protected listing routes (per-subcategory) */}
              <Route path="/auto/cars/list" element={
                <ProtectedRoute>
                  <AutoListLandingPage slug="cars" />
                </ProtectedRoute>
              } />
              <Route path="/auto/cars/list/free" element={
                <ProtectedRoute>
                  <AddFreeVehicleListingPage />
                </ProtectedRoute>
              } />
              <Route path="/auto/cars/list/plus" element={
                <ProtectedRoute>
                  <AddVehicleListingPage />
                </ProtectedRoute>
              } />
              {/* Backwards-compat redirects for old /auto/list routes */}
              <Route path="/auto/list" element={<Navigate to="/auto/cars/list" replace />} />
              <Route path="/auto/list/free" element={<Navigate to="/auto/cars/list/free" replace />} />
              <Route path="/auto/list/plus" element={<Navigate to="/auto/cars/list/plus" replace />} />

              {/* Protected Routes - New Standardized Routes */}

              {/* BUY Routes */}
              <Route path="/my-wants" element={<Navigate to="/my-items" replace />} />
              <Route path="/post-want" element={
                <ProtectedRoute>
                  <CreateWTBPage />
                </ProtectedRoute>
              } />
              
              {/* SELL Routes */}
              <Route path="/list" element={
                <ProtectedRoute>
                  <ListLandingPage />
                </ProtectedRoute>
              } />
              <Route path="/list/item" element={
                <ProtectedRoute>
                  <AddInventoryPage mode="single" />
                </ProtectedRoute>
              } />
              <Route path="/list/photos" element={
                <ProtectedRoute>
                  <AddInventoryPage mode="photos" />
                </ProtectedRoute>
              } />
              <Route path="/my-items" element={
                <ProtectedRoute>
                  <OwnerDashboard />
                </ProtectedRoute>
              } />
              <Route path="/item/:id" element={
                <ProtectedRoute>
                  <InventoryDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/item/:itemId/matches" element={
                <ProtectedRoute>
                  <ItemMatchesPage />
                </ProtectedRoute>
              } />
              
              {/* Dashboard redirect */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              
              {/* Legacy Route Redirects - Preserve backwards compatibility */}
              <Route path="/dashboard/wanter" element={<Navigate to="/my-wants" replace />} />
              <Route path="/dashboard/owner" element={<Navigate to="/my-items" replace />} />
              <Route path="/create-wtb" element={<Navigate to="/post-want" replace />} />
              <Route path="/add-inventory" element={<Navigate to="/list/photos" replace />} />
              <Route path="/list-item" element={<Navigate to="/list/item" replace />} />
              <Route path="/inventory/:id" element={<Navigate to="/item/:id" replace />} />
              <Route path="/purchases" element={
                <ProtectedRoute>
                  <PurchaseHistoryPage />
                </ProtectedRoute>
              } />
              <Route path="/account/billing" element={
                <ProtectedRoute>
                  <BillingPage />
                </ProtectedRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              } />
              <Route path="/wants/:wantId/matches" element={
                <ProtectedRoute>
                  <WantMatchesPage />
                </ProtectedRoute>
              } />
              <Route path="/matches" element={<Navigate to="/my-wants" replace />} />
              <Route path="/matches/:id" element={
                <ProtectedRoute>
                  <MatchDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/messages" element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              } />
              <Route path="/messages/:matchId" element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } />
              <Route path="/premium" element={
                <ProtectedRoute>
                  <PremiumPage />
                </ProtectedRoute>
              } />
              <Route path="/points" element={
                <ProtectedRoute>
                  <PointsPage />
                </ProtectedRoute>
              } />
              <Route path="/wallet" element={
                <ProtectedRoute>
                  <WalletPage />
                </ProtectedRoute>
              } />
              <Route path="/preauth/:preauth_id/confirm" element={
                <ProtectedRoute>
                  <PreAuthConfirmPage />
                </ProtectedRoute>
              } />
              <Route path="/preauth/:preauth_id/remainder/confirm" element={
                <ProtectedRoute>
                  <PreAuthRemainderConfirmPage />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <AdminProtectedRoute>
                  <AdminPage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <AdminProtectedRoute>
                  <AdminUsersPage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <AdminProtectedRoute>
                  <AdminSettingsPage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/reports" element={<Navigate to="/admin/moderation/reports" replace />} />
              <Route path="/admin/moderation/reports" element={
                <AdminProtectedRoute>
                  <AdminCommunityReportsPage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/moderation/detections" element={
                <AdminProtectedRoute>
                  <AdminDetectionsPage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/moderation/discipline" element={
                <AdminProtectedRoute>
                  <AdminDisciplinePage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/audit-logs" element={
                <AdminProtectedRoute>
                  <AdminAuditLogsPage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/analytics" element={
                <AdminProtectedRoute>
                  <AdminAnalyticsPage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/analytics/revenue" element={
                <AdminProtectedRoute>
                  <AdminRevenuePage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/analytics/subscriptions" element={
                <AdminProtectedRoute>
                  <AdminSubscriptionsPage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/billing" element={
                <AdminProtectedRoute>
                  <AdminBillingPage />
                </AdminProtectedRoute>
              } />

              {/* Fallback - 404 Page */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
              </CursorToastProvider>
          </AIProcessingProvider>
        </LocationProvider>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
